// An imported recording turned into the text a note is generated from —
// as the iOS app does (AudioFileTranscriber.swift): transcribed here, in
// the browser, and sent as a .txt; the audio itself never leaves the page.
//
// The audio is decoded and resampled to Whisper's 16 kHz mono by the
// browser's own AudioContext, then read by a Web Worker
// (workers/whisper.worker.js) so the page stays responsive meanwhile.

import { AUDIO_EXTENSIONS, extensionOf } from "./importFileTypes";

const SAMPLE_RATE = 16000;

/** A recording, by its extension — not its MIME type, which calls an .mp4
 *  video `audio/mp4` on some systems. */
export function isAudioFile(file) {
  return AUDIO_EXTENSIONS.includes(extensionOf(file));
}

let worker = null;
let nextID = 0;

function whisperWorker() {
  if (!worker) {
    worker = new Worker(new URL("../workers/whisper.worker.js", import.meta.url), { type: "module" });
  }
  return worker;
}

/** The recording as 16 kHz mono samples. */
async function decode(file) {
  const bytes = await file.arrayBuffer();
  const Context = window.AudioContext || window.webkitAudioContext;
  const context = new Context({ sampleRate: SAMPLE_RATE });
  try {
    const decoded = await context.decodeAudioData(bytes);
    if (decoded.numberOfChannels === 1) return decoded.getChannelData(0);
    // Stereo and beyond: the channels averaged into one.
    const mono = new Float32Array(decoded.length);
    for (let c = 0; c < decoded.numberOfChannels; c++) {
      const channel = decoded.getChannelData(c);
      for (let i = 0; i < channel.length; i++) mono[i] += channel[i] / decoded.numberOfChannels;
    }
    return mono;
  } finally {
    context.close().catch(() => {});
  }
}

/** An error the form can explain: `code` and the file it is about. */
function transcriptionError(code, file, cause) {
  const error = new Error(cause || code);
  error.code = code;
  error.fileName = file.name || "";
  return error;
}

/**
 * The words spoken in `file`. `onProgress` hears `{ phase: "loading",
 * progress }` while the model downloads (first time only), then
 * `{ phase: "transcribing" }`.
 */
export async function transcribeAudioFile(file, { language, onProgress } = {}) {
  let audio;
  try {
    audio = await decode(file);
  } catch (error) {
    throw transcriptionError("audio-unreadable", file, error?.message);
  }
  const id = ++nextID;
  const target = whisperWorker();
  return new Promise((resolve, reject) => {
    const listen = ({ data }) => {
      if (data.id !== id) return;
      if (data.status === "loading") onProgress?.({ phase: "loading", progress: data.progress });
      else if (data.status === "transcribing") onProgress?.({ phase: "transcribing" });
      else {
        target.removeEventListener("message", listen);
        if (data.status === "done") {
          // Silence or music comes back as "..." or "[Music]": words have
          // letters or digits.
          if (/[\p{L}\p{N}]/u.test((data.text || "").replace(/\[[^\]]*\]/g, ""))) resolve(data.text);
          else reject(transcriptionError("audio-empty", file));
        } else {
          reject(transcriptionError("audio-failed", file, data.message));
        }
      }
    };
    target.addEventListener("message", listen);
    // The samples are handed over, not copied.
    target.postMessage({ id, audio, language }, [audio.buffer]);
  });
}

/** The transcript as the text file the note is generated from. */
export function transcriptFile(file, text) {
  const base = (file.name || "audio").replace(/\.[^.]+$/, "") || "audio";
  return new File([text], `${base}.txt`, { type: "text/plain" });
}
